import React, {
  useState, useContext, useCallback
} from 'react';
import { string, bool } from 'prop-types';
import classNames from 'classnames';

import { QueryProvider } from '@thd-nucleus/data-sources';
import { ProductPod } from '@thd-olt-component-react/product-pod';
import { ExperienceContext } from '@thd-nucleus/experience-context';

import {
  buildUpdateCartRequest, generatePromoCartItemsObj, buildAddToCartRequest
} from '../../utils/promo-cart-utils';
import { usePromoCart } from '../../contexts/PromoCartProvider';
import { usePromoModel } from '../../contexts/PromoModelProvider';
import { usePromoPresentation } from '../../contexts/PromoPresentationProvider';
import { PromoQuickview } from '../promo-quickview/PromoQuickview';
import { CallToActionSection } from './subcomponents/CallToActionSection/CallToActionSection';
import './PromoPod.scss';

export const PromoPod = ({ itemId, selected, displayPosition, isInPromoList }) => {
  const {
    isBogo, isMsb, isBmsm, allItemIds, products, storeId, storeZip, singleItemIdToDisplay, analyticsSharedSection
  } = usePromoModel();
  const {
    promoCartItemsObj,
    setBogoSelectedProduct,
    freeItemInCart,
    CartModel,
    addToCart,
    updateCart,
    isCartLoading,
    freezeCart
  } = usePromoCart();

  const { channel } = useContext(ExperienceContext);
  const { isAppleTouchDevice, setIsDrawerOpen } = usePromoPresentation();
  const [isQuickviewOpen, setIsQuickviewOpen] = useState(false);
  const [renderQuickview, setRenderQuickview] = useState(false);
  const [quantityWarningText, setQuantityWarningText] = useState('');

  const product = products.get(itemId);
  const inCartQuantity = promoCartItemsObj?.[itemId]?.quantity || 0;
  const showQuantityWarningState = !!quantityWarningText;

  const onSelect = useCallback(() => {
    if (freeItemInCart && freeItemInCart.itemId !== product.itemId) {
      const store = { storeZip, storeId };
      const { CartRequest: updateCartRequest } = buildUpdateCartRequest({
        promoItemModel: promoCartItemsObj[freeItemInCart.itemId],
        desiredQuantity: 0,
        store
      });

      const unFreezeCart = freezeCart();
      const updatePromise = updateCartRequest.itemDetails.length
        ? updateCart(updateCartRequest)
        : Promise.resolve(CartModel);

      updatePromise
        .then((newCartModel) => {
          const promoCartItems = generatePromoCartItemsObj({
            itemModels: newCartModel?.itemModels,
            allItemIds
          });

          const addToCartRequest = buildAddToCartRequest({
            desiredQuantity: 1,
            promoItemModel: promoCartItems[product.itemId],
            channel,
            product,
            store
          });

          if (addToCartRequest.CartRequest.itemDetails.length) return addToCart(addToCartRequest);

          return Promise.resolve();
        })
        .finally(() => {
          unFreezeCart();
        });
    } else {
      setBogoSelectedProduct(!selected ? { ...product, displayPosition } : null);
    }
  }, [
    freeItemInCart,
    product,
    promoCartItemsObj,
    storeZip,
    storeId,
    updateCart,
    allItemIds,
    channel,
    addToCart,
    setBogoSelectedProduct,
    selected,
    displayPosition,
    CartModel,
    freezeCart
  ]);

  const loadQuickview = useCallback((event) => {
    if (event) event.preventDefault();
    setRenderQuickview(true);
  }, [setRenderQuickview]);

  const openQuickview = useCallback((event) => {
    if (event) event.preventDefault();
    setIsDrawerOpen(true);
    setRenderQuickview(true);
    setIsQuickviewOpen(true);
  }, [setRenderQuickview, setIsQuickviewOpen, setIsDrawerOpen]);

  const miniOptions = {
    disabled: isCartLoading,
    enableATC: false,
    onlyShowIfFulfillable: true,
    silentATC: true,
    inCartQuantity: !isBogo ? inCartQuantity : 0,
    onAction: openQuickview,
    callToActionSection: <CallToActionSection
      isBogo={isBogo}
      isMsb={isMsb}
      isBmsm={isBmsm}
      quantityWarningText={quantityWarningText}
      onSelect={onSelect}
      selected={selected}
      inCartQuantity={inCartQuantity}
      itemId={itemId}
      setQuantityWarningText={setQuantityWarningText}
      showQuantityWarningState={showQuantityWarningState}
      singleItemIdToDisplay={singleItemIdToDisplay}
      displayPosition={displayPosition}
    />,
  };

  const promoPodClasses = classNames('promo-pod', {
    'promo-pod--selected': selected,
    'promo-pod--buybox': !isInPromoList,
  });

  const optionsHandler = ({ options, queryName }) => {
    if (queryName === 'reviews') return options;
    return {
      ...options,
      fetchPolicy: 'cache-only'
    };
  };

  const defaultVariables = {
    skipInstallServices: true,
    skipSpecificationGroup: false,
    storeId,
    zipCode: storeZip
  };

  if (!product) return null;

  return (
    <QueryProvider queryOptions={optionsHandler} defaultVariables={defaultVariables}>
      <section
        className={promoPodClasses}
        id={`promo-pod-${itemId}`}
        data-testid={`promo-pod-${itemId}`}
        role="button"
        tabIndex={0}
        onMouseEnter={!isAppleTouchDevice ? loadQuickview : undefined}
      >
        <ProductPod
          key={itemId}
          itemId={itemId}
          titleMaxLine={2}
          podType="mini"
          miniOptions={miniOptions}
          storeId={storeId}
          onClick={openQuickview}
          showDisabledATC
          ignoreAnalytics
          sharedSection={analyticsSharedSection}
        />
      </section>
      {renderQuickview && (
        <PromoQuickview
          isQuickviewOpen={isQuickviewOpen}
          setIsQuickviewOpen={setIsQuickviewOpen}
          product={product}
          storeId={storeId}
          onSelect={onSelect}
          selected={selected}
          isInPromoList={isInPromoList}
          setQuantityWarningText={setQuantityWarningText}
          showQuantityWarningState={showQuantityWarningState}
          displayPosition={displayPosition}
        />
      )}
    </QueryProvider>
  );
};

PromoPod.displayName = 'PromoPod';

PromoPod.propTypes = {
  itemId: string.isRequired,
  selected: bool,
  displayPosition: string.isRequired,
  isInPromoList: bool,
};

PromoPod.defaultProps = {
  selected: false,
  isInPromoList: true,
};
